import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { subscribeInstallPrompt, promptInstall } from '../utils/installPrompt';
import { getPlayStoreUrl } from '../utils/runtimeConfig';
import { isNativeApp } from '../utils/nativeApp';
import { subscribeConsent } from '../utils/consentStore';
import { isGoogleAdsEnabled } from '../utils/googleAds';
import { isPublicMarketingPath } from '../constants/publicRoutes';
import { moveFocusAfterDismiss } from '../utils/focusAfterDismiss';
import { useBannerHeightCssVar } from '../hooks/useBannerHeightCssVar';
import PoliteAnnouncer from './PoliteAnnouncer';
import './InstallAppBanner.css';

const DISMISSED_STORAGE_KEY = 'rentinger.installBannerDismissedAt';
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 Tage
const BANNER_VISIBLE_CLASS = 'install-banner-visible';

const isAndroidBrowser = () => /Android/i.test(navigator.userAgent);

const isStandaloneDisplayMode = () =>
  window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;

const isRecentlyDismissed = () => {
  try {
    const dismissedAt = Number(window.localStorage.getItem(DISMISSED_STORAGE_KEY));
    return Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch (e) {
    // localStorage kann in Capacitor-/Privacy-Umgebungen werfen (vgl. ErrorBoundary.js).
    return false;
  }
};

/**
 * Fixed-Bottom-Banner, der Android-Browser-Nutzern die Installation der App
 * anbietet. Zwei Modi:
 *  - Play-Store-Modus: REACT_APP_PLAY_STORE_URL ist konfiguriert -> Link zum Store.
 *  - PWA-Modus: beforeinstallprompt wurde gefangen -> nativer Install-Dialog.
 *
 * iOS Safari kennt kein beforeinstallprompt; der Banner ist bewusst auf
 * Android beschränkt (Anforderung: Android-Smartphone/-Tablet).
 */
const InstallAppBanner = () => {
  const location = useLocation();
  // Erst nach dem Mount sichtbar werden: vermeidet Hydration-Mismatch auf
  // prerenderten Seiten (index.js nutzt hydrateRoot). Die Eligibility wird
  // bewusst nur einmal pro Mount berechnet — ein Moduswechsel (Installation,
  // Dismiss in anderem Tab) wird beim nächsten Seitenladen wirksam.
  const [eligible, setEligible] = useState(false);
  const [installPromptAvailable, setInstallPromptAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // null ist zugleich der sichere Initialwert: Solange der persistierte
  // Zustand noch nicht gelesen wurde, kann der Installationsbanner einen
  // moeglichen Cookie-Banner nicht kurzzeitig ueberlagern.
  const [marketingConsent, setMarketingConsent] = useState(null);
  const playStoreUrl = getPlayStoreUrl();

  useEffect(() => {
    setEligible(
      isAndroidBrowser() &&
        !isStandaloneDisplayMode() &&
        !isNativeApp() &&
        !isRecentlyDismissed()
    );
    const unsubscribeInstallPrompt = subscribeInstallPrompt((prompt) => {
      setInstallPromptAvailable(Boolean(prompt));
    });
    return () => {
      unsubscribeInstallPrompt();
    };
  }, []);

  useEffect(() => {
    return subscribeConsent(setMarketingConsent);
  }, []);

  const cookieConsentBannerVisible =
    marketingConsent === null &&
    isGoogleAdsEnabled() &&
    isPublicMarketingPath(location.pathname);

  const show =
    eligible &&
    !dismissed &&
    !cookieConsentBannerVisible &&
    Boolean(playStoreUrl || installPromptAvailable);

  // Solange der Banner sichtbar ist, unten Platz reservieren, damit er keine
  // Inhalte am Seitenende dauerhaft verdeckt (Padding/scroll-padding via CSS).
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    document.documentElement.classList.add(BANNER_VISIBLE_CLASS);
    return () => document.documentElement.classList.remove(BANNER_VISIBLE_CLASS);
  }, [show]);

  // MAJOR-5-Fix (analog zu CookieConsentBanner.js): 140px (CSS-Fallback)
  // reicht ebenfalls nicht in jedem Fall. Anders als beim CookieConsentBanner
  // umbricht hier NICHT der gesamte Banner (kein flex-wrap auf
  // .install-app-banner) — Icon, Text und Actions bleiben in einer Zeile.
  // Die Actions-Spalte (flex-shrink: 0) behält dabei aber ihre volle
  // Eigenbreite ("Aus Google Play laden" ist deutlich breiter als
  // "Später"), wodurch der Textblock (min-width: 0, flex-shrink: 1) auf
  // sehr schmalen Viewports (~320px) auf einen schmalen Rest-Streifen
  // gequetscht wird und in viele Zeilen umbricht — die Textspalte kann die
  // Höhe der Actions-Spalte (2 × 44px + Gap ≈ 96px) dabei durchaus
  // übersteigen. Da der Mechanismus bereits als Hook existiert, kostet die
  // analoge Anwendung hier nichts zusätzlich.
  const bannerRef = useBannerHeightCssVar(show, '--install-banner-height');

  // MAJOR-3-Fix: gleiches PoliteAnnouncer-Muster wie beim CookieConsentBanner
  // (siehe dort für die ausführliche Begründung) — besonders relevant hier,
  // weil dieser Banner unmittelbar nach der Consent-Entscheidung an genau
  // der Stelle erscheint, wohin moveFocusAfterDismiss() gerade den Fokus
  // verschoben hat.
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    setAnnouncement(
      show
        ? 'Hinweis am Seitenende: Rentinger als App nutzen. Schneller Zugriff direkt vom ' +
            'Startbildschirm.'
        : ''
    );
  }, [show]);

  const handleDismiss = () => {
    try {
      window.localStorage.setItem(DISMISSED_STORAGE_KEY, String(Date.now()));
    } catch (e) {
      // Ohne localStorage gilt der Dismiss nur für die laufende Sitzung.
    }
    setDismissed(true);
    moveFocusAfterDismiss();
  };

  const handleInstall = async () => {
    await promptInstall();
    // promptInstall verbraucht das Event -> installPromptAvailable wird false
    // und der Banner verschwindet unabhängig vom Ergebnis.
    moveFocusAfterDismiss();
  };

  return (
    <>
      {/* Muss unabhängig von "show" gemountet sein (siehe PoliteAnnouncer-
          JSDoc) — deshalb außerhalb der show-Bedingung unten. */}
      <PoliteAnnouncer id="install-app-banner-announcer" text={announcement} />
      {show && (
        <div
          ref={bannerRef}
          id="install-app-banner"
          className="install-app-banner shadow-lg"
          role="region"
          aria-label="App-Installation"
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
            alt=""
            className="install-app-banner-icon"
          />
          <div className="install-app-banner-text">
            <strong>Rentinger als App nutzen</strong>
            <span>Schneller Zugriff direkt vom Startbildschirm.</span>
          </div>
          <div className="install-app-banner-actions">
            {playStoreUrl ? (
              // Bewusst ein echter Link (kein <Button as="a">): react-bootstrap
              // würde role="button" setzen, ohne dass die Leertaste navigiert —
              // Rolle und Verhalten müssen zusammenpassen (WCAG 4.1.2).
              <a
                id="install-app-banner-play-link"
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <i className="bi bi-google-play me-1" aria-hidden="true"></i>
                Aus Google Play laden
                <span className="visually-hidden"> (öffnet in neuem Tab)</span>
              </a>
            ) : (
              <Button
                id="install-app-banner-install-button"
                variant="primary"
                onClick={handleInstall}
              >
                <i className="bi bi-download me-1" aria-hidden="true"></i>
                Installieren
              </Button>
            )}
            <Button
              id="install-app-banner-dismiss-button"
              variant="outline-secondary"
              onClick={handleDismiss}
            >
              Später
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppBanner;
